import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { AutoAwesome as AutoAwesomeIcon } from "@mui/icons-material";
import { FaAppleAlt } from "react-icons/fa";
import useAuth from "../../../hooks/useAuth";
import { useAlert } from "../../../context/AlertProvider";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { ChipListSelect, FormTextField } from "../../../components/atoms";
import { DrawerField, tileInputProps } from "../../../components/molecules";
import TemplateListPage, { statusParam } from "../../../components/organisms/TemplateListPage";
import TemplateEditorDrawer from "../../../components/organisms/TemplateEditorDrawer";
import { StatusPill } from "../../../components/organisms/ClinicRecordList";
import { fullDateTime, relativePast } from "../../Secretary/PatientsPage/patientListFormat";
import {
  useCreateDoctorDietTemplateMutation,
  useListDoctorDietTemplatesMutation,
  useSetDoctorDietTemplateStatusMutation,
  useUpdateDoctorDietTemplateMutation,
} from "../../../store/doctorApi";
import { useListFoodsMutation } from "../../../store/systemVariablesApi";

export default function DoctorDietTemplates() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", allowedFoods: [], forbiddenFoods: [] });

  const [foodTemplates, setFoodTemplates] = useState([]);
  const bootRef = useRef(false);

  const [listDoctorDietTemplates] = useListDoctorDietTemplatesMutation();
  const [createDoctorDietTemplate, { isLoading: isCreateLoading }] =
    useCreateDoctorDietTemplateMutation();
  const [updateDoctorDietTemplate, { isLoading: isUpdateLoading }] =
    useUpdateDoctorDietTemplateMutation();
  const [setDoctorDietTemplateStatus] = useSetDoctorDietTemplateStatusMutation();
  const [listFoods] = useListFoodsMutation();

  const isSaving = isCreateLoading || isUpdateLoading;

  const fetchList = async () => {
    if (!token) return;
    setErrorMsg("");
    setIsLoading(true);
    try {
      const res = await listDoctorDietTemplates({
        token,
        query,
        status: statusParam(status),
        page: page + 1,
        limit: rowsPerPage,
      }).unwrap();
      setItems(res?.data?.templates || []);
      setTotal(res?.data?.pagination?.total || 0);
    } catch (e) {
      setErrorMsg(e?.data?.message || "Şablonlar alınamadı.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFoods = async () => {
    if (!token) return;
    try {
      const res = await listFoods({ token, status: "active", limit: 500 }).unwrap();
      setFoodTemplates(res?.data?.foods || []);
    } catch (e) {
      setFoodTemplates([]);
    }
  };

  useEffect(() => {
    if (!token || bootRef.current) return;
    bootRef.current = true;
    fetchList();
    fetchFoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, status, page, rowsPerPage]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", allowedFoods: [], forbiddenFoods: [] });
    setOpen(true);
  };

  const openEdit = (template) => {
    const allowedFoods = (template.allowedList || []).map((it) => {
      return (
        foodTemplates.find((f) => String(f._id) === String(it.food)) || {
          _id: it.food,
          name: it.name,
        }
      );
    });
    const forbiddenFoods = (template.forbiddenList || []).map((it) => {
      return (
        foodTemplates.find((f) => String(f._id) === String(it.food)) || {
          _id: it.food,
          name: it.name,
        }
      );
    });
    setEditing(template);
    setForm({ name: template.name || "", allowedFoods, forbiddenFoods });
    setOpen(true);
  };

  const autoGenerate = () => {
    if (foodTemplates.length === 0) return;
    const half = Math.ceil(foodTemplates.length / 2);
    const shuffled = [...foodTemplates].sort(() => Math.random() - 0.5);
    const allowed = shuffled.slice(0, Math.min(Math.floor(Math.random() * 4) + 2, half));
    const forbidden = shuffled.slice(half, half + Math.floor(Math.random() * 3) + 1);
    setForm({
      name: `Test Diyet Şablonu ${Math.floor(Math.random() * 900) + 100}`,
      allowedFoods: allowed,
      forbiddenFoods: forbidden,
    });
  };

  const onSave = async () => {
    if (!token || !form.name.trim()) return;
    const allowedList = (form.allowedFoods || []).map((f) => ({
      food: f._id,
      name: f.name,
    }));
    const forbiddenList = (form.forbiddenFoods || []).map((f) => ({
      food: f._id,
      name: f.name,
    }));
    try {
      if (editing) {
        await updateDoctorDietTemplate({
          token,
          templateId: editing._id,
          name: form.name,
          allowedList,
          forbiddenList,
        }).unwrap();
        showSuccess("Şablon başarıyla güncellendi");
      } else {
        await createDoctorDietTemplate({
          token,
          name: form.name,
          allowedList,
          forbiddenList,
        }).unwrap();
        showSuccess("Şablon başarıyla oluşturuldu");
      }
      setOpen(false);
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Kaydetme işlemi başarısız.");
    }
  };

  const [togglingStatus, setTogglingStatus] = useState(false);
  const onToggleStatus = async (template) => {
    if (!token) return;
    setTogglingStatus(true);
    try {
      await setDoctorDietTemplateStatus({
        token,
        templateId: template._id,
        isActive: !template.isActive,
      }).unwrap();
      showSuccess(template.isActive ? "Şablon pasife alındı" : "Şablon aktifleştirildi");
      setEditing((prev) => (prev && prev._id === template._id ? { ...prev, isActive: !template.isActive } : prev));
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Durum güncellenemedi.");
    } finally {
      setTogglingStatus(false);
    }
  };

  const canSave = useMemo(() => form.name.trim().length > 2, [form.name]);

  const subText = { fontSize: TYPO.caption, color: COLORS.slate500 };
  const listNames = (list) => (list || []).map((i) => i?.name).filter(Boolean);

  return (
    <TemplateListPage
      icon={<FaAppleAlt size={20} />}
      title="Diyet Şablonları"
      subtitle="Diyet listelerini hızlıca uygulamak için şablonlar"
      query={query}
      onQueryChange={(v) => {
        setPage(0);
        setQuery(v);
      }}
      status={status}
      onStatusChange={(v) => {
        setPage(0);
        setStatus(v);
      }}
      onCreate={openCreate}
      errorMsg={errorMsg}
      isLoading={isLoading}
      columns={[
        { key: "name", label: "Şablon", width: "34%" },
        { key: "foods", label: "Besinler", width: "38%" },
        { key: "updated", label: "Güncellendi", width: "14%" },
        { key: "status", label: "Durum", width: "14%" },
      ]}
      rows={items}
      onRowClick={openEdit}
      renderRowCells={(row) => {
        const allowed = listNames(row.allowedList);
        const forbidden = listNames(row.forbiddenList);
        const allowedCount = row.allowedList?.length || 0;
        const forbiddenCount = row.forbiddenList?.length || 0;
        const preview = allowed.length
          ? allowed.slice(0, 3).join(", ")
          : `Yasaklı: ${forbidden.slice(0, 3).join(", ")}`;
        const updated = row.updatedAt || row.createdAt;
        return [
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }} noWrap>
            {row.name}
          </Typography>,
          allowed.length || forbidden.length ? (
            <Box sx={{ minWidth: 0, pr: 2 }}>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} noWrap>
                {preview}
              </Typography>
              <Typography sx={subText}>
                {allowedCount} izinli · {forbiddenCount} yasaklı
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          updated ? (
            <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} title={fullDateTime(updated)}>
              {relativePast(updated)}
            </Typography>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          <StatusPill label={row.isActive ? "Aktif" : "Pasif"} tone={row.isActive ? "success" : "neutral"} />,
        ];
      }}
      emptyIcon={<FaAppleAlt size={22} />}
      emptyMessage="İzinli ve yasaklı besin listelerini şablon olarak kaydedin."
      page={page}
      rowsPerPage={rowsPerPage}
      total={total}
      onPageChange={(_, newPage) => setPage(newPage)}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
    >
      <TemplateEditorDrawer
        open={open}
        onClose={() => setOpen(false)}
        icon={<FaAppleAlt size={18} />}
        title={editing ? "Şablonu düzenle" : "Yeni diyet şablonu"}
        subtitle={editing ? editing.name : "Beslenme planını hızlıca belirlemek için şablon oluşturun"}
        onSubmit={onSave}
        submitLabel={editing ? "Güncelle" : "Kaydet"}
        submitDisabled={!canSave}
        loading={isSaving}
        statusControl={
          editing
            ? { isActive: Boolean(editing.isActive), onToggle: () => onToggleStatus(editing), loading: togglingStatus }
            : null
        }
        headerExtra={
          !editing ? (
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              size="small"
              onClick={autoGenerate}
              disabled={foodTemplates.length === 0}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: COLORS.warning,
                color: COLORS.warningDark,
                "&:hover": { bgcolor: COLORS.warningLightest, borderColor: COLORS.warningDark },
              }}
            >
              Otomatik doldur (test)
            </Button>
          ) : null
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <DrawerField label="Şablon adı *" hint="En az 3 karakter.">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              placeholder="Ör. Düşük karbonhidrat"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              InputProps={tileInputProps(true)}
            />
          </DrawerField>
          <ChipListSelect
            label="İzinli besinler"
            placeholder="Besin ara"
            options={foodTemplates}
            value={form.allowedFoods}
            onChange={(value) => setForm((p) => ({ ...p, allowedFoods: value || [] }))}
            emptyText="Henüz izinli besin eklenmedi."
          />
          <ChipListSelect
            label="Yasaklı besinler"
            placeholder="Besin ara"
            options={foodTemplates}
            value={form.forbiddenFoods}
            onChange={(value) => setForm((p) => ({ ...p, forbiddenFoods: value || [] }))}
            emptyText="Henüz yasaklı besin eklenmedi."
          />
        </Box>
      </TemplateEditorDrawer>
    </TemplateListPage>
  );
}
